import type { QueryKey } from '@tanstack/react-query';
import { env } from 'core/constants/env.constants';

const API_PREFIX = 'api';
const API_VERSION = 'v1';
const API_HOST = env.VITE_API_HOST;
const BASE_URL = `${API_HOST}/${API_PREFIX}/${API_VERSION}`;
const BASE_URL_V2 = `${API_HOST}/${API_PREFIX}/v2`;

enum Controller {
  trainingPrograms = '/trainingprograms',
  operators = '/operators',
  settings = '/settings',
}

/**
 * API routes dictionary
 */
export const apiRoutes = {
  trainingPrograms: {
    getAll: () => `${BASE_URL}${Controller.trainingPrograms}`,
    getById: (id: string): string => `${BASE_URL}${Controller.trainingPrograms}/${id}`,
  },
  stages: {
    getById: (trainingProgramId: string, stageId: string): string =>
      `${BASE_URL}${Controller.trainingPrograms}/${trainingProgramId}/stages/${stageId}`,
  },
  operators: {
    search: (): string => `${BASE_URL}${Controller.operators}/search`,
    getOperatorEnrollmentsSummary: (operatorId: string): string =>
      `${BASE_URL}${Controller.operators}/${operatorId}/enrollments`,
    getOperatorEnrollmentDetail: (operatorId: string, enrollmentId: string): string =>
      `${BASE_URL}${Controller.operators}/${operatorId}/enrollments/${enrollmentId}`,
    getOperatorsEnrollmentsSummary: (): string => `${BASE_URL}${Controller.operators}/enrollments`,
    multiOperatorEnrollmentsVerify: (): string =>
      `${BASE_URL_V2}${Controller.operators}/enrollments/verify`,
    singleOperatorEnrollmentVerify: (operatorId: string, enrollmentId: string): string =>
      `${BASE_URL_V2}${Controller.operators}/${operatorId}/enrollments/${enrollmentId}/tasks/verify`,
  },
  settings: {
    get: (): string => `${BASE_URL}${Controller.settings}`,
    saveLanguage: (): string => `${BASE_URL}${Controller.settings}/language`,
  },
};

/**
 * API keys dictionary
 *
 * Dictionary with unique base keys intended to identify group of queries for react-query
 */
export const apiBaseKeys = {
  trainingPrograms: 'trainingPrograms',
  operators: 'operators',
  settings: 'settings',
  languages: 'languages',
};

/**
 * API keys dictionary
 *
 * Dictionary with unique keys intended to identify queries for react-query
 */
export const apiKeys = {
  trainingPrograms: {
    getAll: (): string => `${apiBaseKeys.trainingPrograms}.getAll`,
    getById: (id: string): string => `${apiBaseKeys.trainingPrograms}.getById.${id}`,
  },
  stages: {
    getById: (trainingProgramId: string, stageId: string): string =>
      `${apiBaseKeys.trainingPrograms}.getById.${trainingProgramId}.stage.${stageId}`,
  },
  operators: {
    search: (): string => `${apiBaseKeys.operators}.search`,
    getOperatorEnrollmentsSummary: (operatorId: string): string =>
      `${apiBaseKeys.operators}.getOperatorEnrollmentsSummary.${operatorId}`,
    getOperatorEnrollmentDetail: (operatorId: string, enrollmentId: string): string =>
      `${apiBaseKeys.operators}.getOperatorEnrollmentSummary.${operatorId}.${enrollmentId}`,
    getOperatorsEnrollmentsSummary: (enrollmentIds: string[]): string =>
      `${apiBaseKeys.operators}.getOperatorsEnrollmentsSummary.${[...enrollmentIds]
        .sort()
        .join('.')}`,
  },
  settings: {
    get: (): string => `${apiBaseKeys.settings}.get`,
  },
};

export const operatorSearchQueryKey = (searchValue: string, taskIds: string[]): QueryKey => [
  apiBaseKeys.operators,
  apiKeys.operators.search(),
  searchValue,
  taskIds.join('.'),
];

export const isRootOperatorSearchQueryKey = ([baseKey, key, , taskIds]: QueryKey): boolean =>
  baseKey === apiBaseKeys.operators && key === apiKeys.operators.search() && taskIds === '';
